<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useGetProductsPaymentsProductsGet } from '@gen/hooks/useGetProductsPaymentsProductsGet';
import { X } from '@lucide/vue';
import PlanTicket from '@app/presentation/components/payments/PlanTicket.vue';
import PaperLoader from '@app/presentation/components/shared/PaperLoader.vue';
import ErrorState from '@app/presentation/components/shared/ErrorState.vue';
import { Button } from '@/components/ui/button';
import { usePaywall } from '@app/composables/usePaywall';
import { usePostHog } from '@core/analytics';
import { usePlatform } from '@core/platform';
const router = useRouter();
const { t, locale } = useI18n();
const {
  data: productsData,
  isError: productsError,
  isFetching: productsFetching,
  refetch: refetchProducts,
} = useGetProductsPaymentsProductsGet({ query: { staleTime: Infinity } });
const { posthog } = usePostHog();
const { vibrate } = usePlatform();
const selectedPlan = ref('');
const { isVisible, hidePaywall } = usePaywall();
// Reset each time the paywall is shown — it stays mounted, so mount time is meaningless.
const startTime = ref(Date.now());
// The plan selected when this showing opened (the preselected default, or a pick kept from an
// earlier showing), so `plan_changed` means "picked another plan in this showing".
const planAtShow = ref('');

const products = computed(() => {
  if (!productsData.value || !productsData.value.length) return [];

  return productsData.value.map(product => ({
    id: product.id,
    period: `profile.subscription.${product.id}`,
    price: product.price.toString(),
    originalPrice: (product.price * 1.25).toFixed(0),
    perWeek: product.price / (product.duration_days / 7),
    popular: product.id === 'MONTH_SUB_V3',
    bestValue: product.id === 'YEAR_SUB_V3',
  }));
});

// Handle payment button click (card / YooKassa — the selected plan, in ₽)
const handlePayment = () => {
  // No plans loaded yet → nothing to buy (an empty plan bounces off /payment).
  if (!products.value.length) return;
  posthog.capture('paywall_payment_clicked', {
    selected_plan: selectedPlan.value,
    plan_id: selectedPlan.value,
    plan_price: products.value.find(p => p.id === selectedPlan.value)?.price,
    time_spent_ms: Date.now() - startTime.value,
  });
  router.push({
    name: 'payment',
    query: { plan: selectedPlan.value || products.value[1]?.id },
  });
  hidePaywall();
};

// Closing (✕, "skip", Esc) works straight away.
const handleClose = () => {
  posthog.capture('paywall_closed', {
    time_spent_ms: Date.now() - startTime.value,
    selected_plan: selectedPlan.value,
    plan_changed: selectedPlan.value !== planAtShow.value,
  });
  hidePaywall();
};

const selectPlan = (plan: (typeof products.value)[0]) => {
  vibrate('soft');
  selectedPlan.value = plan.id;
  posthog.capture('paywall_plan_selected', {
    plan_id: plan.id,
    plan_price: plan.price,
    is_popular: plan.popular,
    is_best_value: plan.bestValue,
  });
};

const getValueComparison = (plan: (typeof products.value)[0]) => {
  if (plan.id === 'WEEK_SUB_V3') return null;
  const weekPlan = products.value.find(p => p.id === 'WEEK_SUB_V3');
  if (!weekPlan) return null;
  const savings = Math.round(((weekPlan.perWeek - plan.perWeek) / weekPlan.perWeek) * 100);
  return savings;
};

// Preselect the monthly ("popular") plan as soon as products are available — the query may resolve
// after mount, so a one-shot assignment would leave every card unhighlighted. Guarded so a manual
// pick is never overridden.
watch(
  products,
  list => {
    if (!selectedPlan.value && list.length) {
      selectedPlan.value = list.find(p => p.popular)?.id ?? list[1]?.id ?? list[0]?.id ?? '';
      // Products can load after the paywall opened — the default is still this showing's start.
      if (!planAtShow.value) planAtShow.value = selectedPlan.value;
    }
  },
  { immediate: true },
);

// Each benefit is written on its own colour of card stock. Computed, so the slides follow
// a language change (the paywall stays mounted in App.vue).
const sliderCards = computed(() => [
  {
    title: t('paywall.slides.all_in_one.title'),
    bullet1: t('paywall.slides.all_in_one.bullet1'),
    bullet2: t('paywall.slides.all_in_one.bullet2'),
    bullet3: t('paywall.slides.all_in_one.bullet3'),
    bullet4: t('paywall.slides.all_in_one.bullet4'),
    stock: 'bg-stock-plum',
  },
  {
    title: t('paywall.slides.uncertainty.title'),
    bullet1: t('paywall.slides.uncertainty.bullet1'),
    bullet2: t('paywall.slides.uncertainty.bullet2'),
    bullet3: t('paywall.slides.uncertainty.bullet3'),
    stock: 'bg-stock-wine',
  },
  {
    title: t('paywall.slides.stuck.title'),
    bullet1: t('paywall.slides.stuck.bullet1'),
    bullet2: t('paywall.slides.stuck.bullet2'),
    bullet3: t('paywall.slides.stuck.bullet3'),
    stock: 'bg-stock-lilac',
  },
  {
    title: t('paywall.slides.lost.title'),
    bullet1: t('paywall.slides.lost.bullet1'),
    bullet2: t('paywall.slides.lost.bullet2'),
    bullet3: t('paywall.slides.lost.bullet3'),
    stock: 'bg-stock-slate',
  },
  {
    title: t('paywall.slides.relationships.title'),
    bullet1: t('paywall.slides.relationships.bullet1'),
    bullet2: t('paywall.slides.relationships.bullet2'),
    bullet3: t('paywall.slides.relationships.bullet3'),
    stock: 'bg-stock-rust',
  },
  {
    title: t('paywall.slides.life.title'),
    bullet1: t('paywall.slides.life.bullet1'),
    bullet2: t('paywall.slides.life.bullet2'),
    bullet3: t('paywall.slides.life.bullet3'),
    stock: 'bg-stock-tide',
  },
]);

const currentSlide = ref(0);
const touchStart = ref<{ x: number; y: number }>({ x: 0, y: 0 });
const swipeThreshold = 50;
const autoSwipeInterval = ref<number | null>(null);
const SWIPE_DELAY = 3000; // 3 seconds

const startAutoSwipe = () => {
  stopAutoSwipe();
  autoSwipeInterval.value = window.setInterval(() => {
    nextSlide();
  }, SWIPE_DELAY);
};

const stopAutoSwipe = () => {
  if (autoSwipeInterval.value) {
    clearInterval(autoSwipeInterval.value);
    autoSwipeInterval.value = null;
  }
};

const nextSlide = () => {
  currentSlide.value = currentSlide.value < sliderCards.value.length - 1 ? currentSlide.value + 1 : 0;
};

const prevSlide = () => {
  currentSlide.value = currentSlide.value > 0 ? currentSlide.value - 1 : sliderCards.value.length - 1;
};

const goToSlide = (index: number) => {
  currentSlide.value = index;
  // Reset timer when manually changing slides
  startAutoSwipe();
};

const onTouchStart = (e: TouchEvent) => {
  stopAutoSwipe();
  touchStart.value = {
    x: e.touches[0].clientX,
    y: e.touches[0].clientY,
  };
};

const onTouchEnd = (e: TouchEvent) => {
  const deltaX = e.changedTouches[0].clientX - touchStart.value.x;
  const deltaY = e.changedTouches[0].clientY - touchStart.value.y;

  // Only trigger if the horizontal swipe exceeds the vertical movement and passes threshold
  if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > swipeThreshold) {
    if (deltaX < 0) {
      nextSlide();
    } else {
      prevSlide();
    }
  }

  // Resume auto-swipe after touch interaction
  startAutoSwipe();
};

// --- Measure maximum card height ---
const cardRefs = ref<HTMLElement[]>([]);
const maxCardHeight = ref(0);

const calculateMaxHeight = () => {
  let maxHeight = 0;
  cardRefs.value.forEach(card => {
    if (card) {
      const h = card.offsetHeight;
      if (h > maxHeight) maxHeight = h;
    }
  });
  maxCardHeight.value = maxHeight;
};

// New copy (another language) → drop the pinned height and measure the slides again.
watch(locale, () => {
  maxCardHeight.value = 0;
  nextTick(calculateMaxHeight);
});

// A modal: focus moves into the dialog when it opens and back when it closes. The dialog
// itself takes focus (not the ✕ chip), so an auto-shown paywall doesn't open with a focus
// ring on the chip; Tab goes on from there.
const dialogEl = ref<HTMLElement | null>(null);
let focusBefore: HTMLElement | null = null;

// Watch for visibility changes to recalculate height when paywall is shown
watch(isVisible, newValue => {
  if (newValue) {
    focusBefore = document.activeElement as HTMLElement | null;
    nextTick(() => dialogEl.value?.focus());
  } else {
    focusBefore?.focus?.();
    focusBefore = null;
  }
  if (newValue) {
    startTime.value = Date.now();
    planAtShow.value = selectedPlan.value;
    // Reset to first slide whenever paywall is shown
    currentSlide.value = 0;

    // Delay calculation slightly to ensure DOM is fully rendered
    setTimeout(() => {
      calculateMaxHeight();
    }, 100);
  }
});

onMounted(() => {
  nextTick(() => {
    calculateMaxHeight();
    window.addEventListener('resize', calculateMaxHeight);
    startAutoSwipe();

    // Add a second calculation with a delay to ensure all content is rendered
    setTimeout(() => {
      calculateMaxHeight();
    }, 300);
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', calculateMaxHeight);
  stopAutoSwipe();
});
</script>

<template>
  <!-- The paywall is the theatre's box office: the benefits on sheets of card stock,
       the plans as paper theatre tickets, the CTA on the torn paper strip. One column
       filling the screen (content scrolls on very short screens), so nothing overlaps. -->
  <div
    v-if="isVisible"
    ref="dialogEl"
    tabindex="-1"
    class="fixed inset-0 z-[9999] bg-background outline-none"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('paywall.title')"
    @keydown.esc="handleClose"
  >
    <div class="relative mx-auto flex h-full w-full flex-col md:max-w-[390px]">
      <button
        type="button"
        class="pw-close absolute right-3 top-3 z-[2] flex h-10 w-10 items-center justify-center"
        :aria-label="$t('paywall.close')"
        @click="handleClose"
      >
        <X
          class="h-4 w-4"
          aria-hidden="true"
        />
      </button>

      <div class="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 pt-5">
        <h1 class="pw-title type-title px-11 text-center">{{ $t('paywall.title') }}</h1>

        <!-- Benefits -->
        <div class="mt-3">
          <div
            class="overflow-hidden px-1 py-2"
            @touchstart="onTouchStart"
            @touchend="onTouchEnd"
          >
            <div
              class="flex transition-transform duration-300 ease-out motion-reduce:transition-none"
              :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
            >
              <div
                v-for="(card, index) in sliderCards"
                :key="index"
                ref="cardRefs"
                class="min-w-full px-1"
                :aria-hidden="currentSlide !== index"
              >
                <div
                  class="paper-sheet text-foreground px-4 py-3.5"
                  :class="card.stock"
                  :style="maxCardHeight ? { height: maxCardHeight + 'px' } : {}"
                >
                  <h2 class="type-app-heading mb-2">{{ card.title }}</h2>
                  <ul class="flex flex-col gap-1.5">
                    <li class="type-label">{{ card.bullet1 }}</li>
                    <li class="type-label">{{ card.bullet2 }}</li>
                    <li class="type-label">{{ card.bullet3 }}</li>
                    <li
                      v-if="card.bullet4"
                      class="type-label"
                    >
                      {{ card.bullet4 }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <!-- Slide tabs: little paper tabs (the current one cream and taller). -->
          <div class="mt-2 flex items-end justify-center gap-1.5">
            <button
              v-for="(card, index) in sliderCards"
              :key="index"
              type="button"
              class="pw-tab flex h-6 items-end"
              :aria-label="card.title"
              :aria-current="currentSlide === index || undefined"
              @click="goToSlide(index)"
            >
              <span :class="['pw-tab-piece', currentSlide === index && 'is-on']" />
            </button>
          </div>
        </div>

        <!-- Plans: theatre tickets (the paws walk in their place until they load; a note if they can't). -->
        <div
          v-if="!products.length && productsError && !productsFetching"
          class="mt-auto pt-6"
        >
          <ErrorState
            retryable
            @retry="refetchProducts()"
          />
        </div>
        <div
          v-else-if="!products.length"
          class="pw-plans-wait mt-auto flex items-center justify-center pt-6"
        >
          <PaperLoader size="sm" />
        </div>
        <div
          v-else
          class="mt-auto grid grid-cols-3 gap-2.5 pt-6"
          role="group"
          :aria-label="$t('paywall.unlockPremiumAccess')"
        >
          <PlanTicket
            v-for="(plan, i) in products"
            :key="plan.id"
            :period="t(plan.period)"
            :price="plan.price"
            :original-price="plan.originalPrice"
            :save="getValueComparison(plan) ? $t('profile.subscription.saveValue', { percent: getValueComparison(plan) }) : undefined"
            :stamp="plan.popular
              ? { label: $t('profile.subscription.popular'), tone: 'wine' }
              : plan.bestValue ? { label: $t('profile.subscription.bestValue'), tone: 'moss' } : undefined"
            :selected="selectedPlan === plan.id"
            :index="i"
            deal
            @click="selectPlan(plan)"
          />
        </div>
      </div>

      <!-- The CTA on a torn paper strip, the skip under it. -->
      <div class="relative shrink-0 animate-button-in">
        <div
          aria-hidden="true"
          class="paper-torn-t absolute inset-x-0 -top-[3px] bottom-0 bg-[var(--paper-shade)]"
        />
        <div class="paper-torn-t relative flex flex-col gap-1 bg-card px-4 pb-5 pt-2">
          <Button
            class="pw-cta fancy-button h-14 w-full p-4 text-base text-white"
            :disabled="!products.length"
            @click="handlePayment"
          >
            {{ $t('paywall.unlockPremiumAccess') }}
          </Button>
          <button
            type="button"
            class="pw-skip type-caption w-full py-2 transition-colors active:text-foreground"
            @click="handleClose"
          >
            {{ $t('paywall.skip') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Brand CTA font, as MainButton's (Tenor Sans, the chosen faux-bold). */
.pw-cta {
  font-family: var(--font-serif);
  font-weight: 700;
}
/* Short phones (SE-class): a smaller title (the tickets tighten too) so the plans and
   the CTA fit without scrolling. Scoped specificity beats the unlayered role. */
@media (max-height: 720px) {
  .pw-title {
    font-size: 1.5rem;
  }
}
/* The loader holds the tickets' place, so nothing jumps when they come in. */
.pw-plans-wait {
  min-height: 176px;
}
@media (max-height: 720px) {
  .pw-plans-wait {
    min-height: 150px;
  }
}
/* Close: a small cream-ringed stock chip. A fixed 40px, so it never grows into the centred
   title's margin. */
.pw-close {
  border-radius: var(--paper-radius-sm);
  background: var(--card);
  color: var(--foreground);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.08), 0 3px 8px -3px var(--paper-shade);
}
.pw-close:focus-visible,
.pw-tab:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 3px;
}
.pw-tab {
  padding: 0 1px;
}
.pw-tab-piece {
  display: block;
  width: 14px;
  height: 10px;
  transform: scaleY(0.6);
  transform-origin: 50% 100%;
  border-radius: 2px 2px 0 0;
  background: color-mix(in srgb, var(--foreground) 22%, transparent);
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.2s;
}
.pw-tab-piece.is-on {
  transform: none;
  background: var(--paper-stock);
  box-shadow: 0 2px 4px -1px var(--paper-shade);
}

@media (prefers-reduced-motion: reduce) {
  .pw-tab-piece {
    transition: none;
  }
}
</style>
